import React from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  Col,
  Row,
  Card,
  Label,
  TabPane,
  Container,
  TabContent,
} from 'reactstrap';

import Meta from 'components/App/Meta';
import Button from 'components/App/Button';
import Loading from 'components/App/Loading';
import Pagination from 'components/Pagination';
import Breadcrumb from 'components/App/Breadcrumb';
import CustomerTable from 'components/App/CustomerTable';
import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import ModalUpgradeCustomerAccount from 'components/Modals/ModalUpgradeCustomerAccount';
import DownloadIcon from 'assets/images/download.svg';
import SearchIcon from 'assets/images/search-icon.svg';
import images from 'assets/images';

import cn from 'utils/classnames';
import { LOCATIONS, themeColor, userGenderSelectList } from 'constants/index';
import {
  CUSTOMER_ACCOUNT_TYPE_LABELS,
  CUSTOMER_ACCOUNT_TYPE_OPTIONS,
  CUSTOMER_LABEL_TO_ACCOUNT_TYPE,
} from 'constants/customerAccountType';
import CustomerPropTypes from 'propTypeModels/customer';

import styles from './Customers.module.scss';

const pageTitle = 'Danh sách khách hàng';

const breadcrumbs = [
  {
    name: pageTitle,
    isActive: true,
    link: LOCATIONS.CUSTOMERS,
  },
];
const genderOptions = userGenderSelectList;

const TAB = {
  ALL: 'ALL',
  BLOCKED: 'BLOCKED',
};

const NAV_TAB = [
  {
    title: 'Đang hoạt động',
    value: TAB.ALL,
  },
  {
    title: 'Đã chặn',
    value: TAB.BLOCKED,
  },
];

const DEFAULT_LIMIT = 10;

const Customers = ({
  customers,
  getCustomers,
  totalCustomers,
  customersLoading,
  provinces,
  actionGetProvinceList,
  exportCustomerFile,
  exportCustomerLoading,
  blockCustomer,
  unblockCustomer,
  updateCustomerLevel,
}) => {
  const history = useHistory();

  const [selectedCustomerIdToBlock, setSelectedCustomerIdToBlock] =
    React.useState(null);
  const [upgradingCustomer, setUpgradingCustomer] = React.useState(null);

  React.useEffect(() => {
    actionGetProvinceList({});
  }, [actionGetProvinceList]);

  const residenceOptions = React.useMemo(() => {
    const list = Array.isArray(provinces) ? provinces : [];
    return list
      .map((province) => ({
        label: province.name,
        value: province.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [provinces]);

  const urlParams = React.useMemo(
    () => new URLSearchParams(history.location.search),
    [history.location.search],
  );

  const activeTab = React.useMemo(() => {
    const isBlockedParam = urlParams.get('isBlocked');
    return isBlockedParam === 'true' ? TAB.BLOCKED : TAB.ALL;
  }, [urlParams]);

  const urlPage = React.useMemo(
    () => Number(urlParams.get('page') || 1),
    [urlParams],
  );

  const totalPage = React.useMemo(
    () => Math.ceil(totalCustomers / DEFAULT_LIMIT),
    [totalCustomers],
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      q: urlParams.get('q') || '',
      level: urlParams.get('level') || '',
      province: urlParams.get('province') || '',
      gender: urlParams.get('gender') || '',
    },
    onSubmit: (values) => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('page', '1');
      Object.entries(values).forEach(([key, value]) => {
        if (value !== '' && value != null) {
          searchParams.set(key, value);
        } else {
          searchParams.delete(key);
        }
      });
      history.push(`?${searchParams.toString()}`);
    },
  });

  const onResetForm = React.useCallback(() => {
    validation.resetForm();
    history.push('?page=1');
  }, [validation, history]);

  const onChangeFilterOption = React.useCallback(
    (e) => {
      validation.setFieldValue(e.name, e.value, true);
    },
    [validation],
  );

  const onChangeTab = React.useCallback(
    (tab) => () => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('page', '1');
      searchParams.set('isBlocked', tab === TAB.BLOCKED ? 'true' : 'false');
      Object.entries(validation.values).forEach(([key, value]) => {
        if (value !== '' && value != null) {
          searchParams.set(key, value);
        } else {
          searchParams.delete(key);
        }
      });
      history.push(`?${searchParams.toString()}`);
    },
    [validation.values, history],
  );

  const onClickExportFile = React.useCallback(() => {
    const payload = {
      isBlocked: activeTab === TAB.BLOCKED,
    };

    const filterKeys = ['q', 'level', 'province', 'gender'];
    filterKeys.forEach((key) => {
      const value = urlParams.get(key);
      if (value !== '' && value != null) {
        payload[key] = value;
      }
    });

    exportCustomerFile(payload);
  }, [activeTab, urlParams, exportCustomerFile]);

  const handleReloadCustomers = React.useCallback(() => {
    const payload = {
      page: urlPage,
      limit: DEFAULT_LIMIT,
      isBlocked: activeTab === TAB.BLOCKED,
    };

    const filterKeys = ['q', 'level', 'province', 'gender'];
    filterKeys.forEach((key) => {
      const value = urlParams.get(key);
      if (value !== '' && value != null) {
        payload[key] = value;
      }
    });

    getCustomers(payload);
  }, [urlPage, activeTab, urlParams, getCustomers]);

  const onClickBlockUser = React.useCallback((arg) => {
    const id =
      arg && typeof arg === 'object'
        ? arg.currentTarget?.value || arg.target?.value
        : arg;
    if (id) {
      setSelectedCustomerIdToBlock(id);
    }
  }, []);

  const onClickUnblockUser = React.useCallback((arg) => {
    const id =
      arg && typeof arg === 'object'
        ? arg.currentTarget?.value || arg.target?.value
        : arg;

    if (id) {
      unblockCustomer({
        id,
        callback: handleReloadCustomers,
      });
    }
  }, [unblockCustomer, handleReloadCustomers]);

  const onCloseModalBlockUser = React.useCallback(() => {
    setSelectedCustomerIdToBlock(null);
  }, []);

  const onConfirmBlockUser = React.useCallback(() => {
    if (selectedCustomerIdToBlock) {
      blockCustomer({
        id: selectedCustomerIdToBlock,
        callback: () => {
          handleReloadCustomers();
          setSelectedCustomerIdToBlock(null);
        },
      });
    }
  }, [selectedCustomerIdToBlock, blockCustomer, handleReloadCustomers]);

  const selectedCustomerToBlock = React.useMemo(
    () => customers.find((customer) => customer._id === selectedCustomerIdToBlock),
    [customers, selectedCustomerIdToBlock],
  );

  const blockCustomerName = selectedCustomerToBlock?.fullName
    || selectedCustomerToBlock?.displayName
    || '';

  const blockCustomerMessage = blockCustomerName
    ? `Bạn có chắc chắn muốn chặn ${blockCustomerName} không?`
    : 'Bạn có chắc chắn muốn chặn người dùng này không?';

  const onClickUpgradeUser = React.useCallback((customer) => {
    setUpgradingCustomer({
      _id: customer._id,
      fullName: customer.fullName || customer.displayName || '',
      accountType: CUSTOMER_ACCOUNT_TYPE_LABELS[customer.level] || customer.level || '',
    });
  }, []);

  const onCloseUpgradeModal = React.useCallback(() => {
    setUpgradingCustomer(null);
  }, []);

  const onSubmitUpgradeUser = React.useCallback((userId, newType) => {
    const level = CUSTOMER_LABEL_TO_ACCOUNT_TYPE[newType] || newType;

    if (!level) return;

    updateCustomerLevel({
      id: userId,
      level,
      callback: () => {
        handleReloadCustomers();
        setUpgradingCustomer(null);
      },
    });
  }, [handleReloadCustomers, updateCustomerLevel]);

  const onChangePage = React.useCallback(
    (page) => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('page', String(page.selected + 1));
      history.push(`?${searchParams.toString()}`);
    },
    [history],
  );

  React.useEffect(() => {
    const payload = {
      page: urlPage,
      limit: DEFAULT_LIMIT,
      isBlocked: activeTab === TAB.BLOCKED,
    };

    const filterKeys = ['q', 'level', 'province', 'gender'];
    filterKeys.forEach((key) => {
      const value = urlParams.get(key);
      if (value !== '' && value != null) {
        payload[key] = value;
      }
    });

    getCustomers(payload);
  }, [urlPage, urlParams, activeTab, getCustomers]);

  return (
    <>
      <Meta name={pageTitle} />

      <div className={`page-content ${styles.Page}`}>
        <Container fluid>
          <Breadcrumb pageTitle={pageTitle} breadcrumbList={breadcrumbs} />

          <Loading loading={customersLoading} loaderOnPage />

          <Card className="Section">
            <form onSubmit={validation.handleSubmit}>
              <Row>
                <Col xxl="6" className="mb-24">
                  <Label className="Section__Search__Label">Tìm kiếm</Label>

                  <div className="Section__Search__Container">
                    <AppInputValidation
                      id="q"
                      name="q"
                      type="text"
                      validation={validation}
                      inputClassName={cn('Section__Search__Input')}
                      placeholder="Tìm theo ID, Email và Số điện thoại của khách hàng"
                    />

                    <img
                      className="fas fa-search Section__Search__Icon__Search"
                      src={SearchIcon}
                      alt="search"
                    />
                  </div>
                </Col>

                <Col xxl="6">
                  <Label className="Section__Search__Label">Bộ lọc</Label>

                  <Row>
                    <Col md="4" className="mb-24">
                      <AppSelectValidation
                        name="level"
                        isHiddenSearchable
                        validation={validation}
                        placeholder="Loại tài khoản"
                        options={CUSTOMER_ACCOUNT_TYPE_OPTIONS}
                        value={validation.values.level}
                        onChange={onChangeFilterOption}
                      />
                    </Col>

                    <Col md="4" className="mb-24">
                      <AppSelectValidation
                        name="province"
                        isHiddenSearchable
                        validation={validation}
                        placeholder="Nơi sinh sống"
                        options={residenceOptions}
                        value={validation.values.province}
                        onChange={onChangeFilterOption}
                      />
                    </Col>

                    <Col md="4" className="mb-24">
                      <AppSelectValidation
                        name="gender"
                        isHiddenSearchable
                        validation={validation}
                        placeholder="Giới tính"
                        options={genderOptions}
                        value={validation.values.gender}
                        onChange={onChangeFilterOption}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              <div className="d-flex">
                <Button
                  outline
                  classes="Section__Search__Button__ReType mr-24"
                  color={themeColor.LIGHT}
                  onClick={onResetForm}
                >
                  Nhập lại
                </Button>

                <Button
                  classes="Section__Search__Button__Search"
                  type="submit"
                  // disabled={isSearchBtnDisabled}
                >
                  Tìm kiếm
                </Button>
              </div>
            </form>
          </Card>

          <Card className="Section">
            <div className="Tab__Header__Container">
              <div className="Tab__Header">
                <nav
                  className="Tab__Header__Nav"
                  aria-label="Trạng thái khách hàng"
                >
                  {NAV_TAB.map((tab) => (
                    <div key={tab.value} className="Tab__Header__Tab">
                      <button
                        type="button"
                        onClick={onChangeTab(tab.value)}
                        className={`Tab__Header__Tab__Item ${
                          activeTab === tab.value ? 'Active' : ''
                        }`}
                      >
                        {tab.title}
                      </button>
                    </div>
                  ))}
                </nav>
              </div>

              <Button
                fontSize="13px"
                classes="Table__Header__Button"
                disabled={exportCustomerLoading}
                color={themeColor.LIGHT}
                onClick={onClickExportFile}
              >
                <img src={DownloadIcon} alt="download icon" />
                Xuất file
              </Button>
            </div>

            <TabContent activeTab={activeTab} className="Table__Body">
              <Label className="Table__Body__Total">
                {`${totalCustomers} khách hàng`}
              </Label>

              {NAV_TAB.map((item) => (
                <TabPane tabId={item.value} key={item.value}>
                  <CustomerTable
                    data={customers}
                    loading={customersLoading}
                    onClickBlockUser={onClickBlockUser}
                    onClickUnblockUser={onClickUnblockUser}
                    onClickUpgradeUser={onClickUpgradeUser}
                  />
                </TabPane>
              ))}
            </TabContent>
          </Card>

          <Pagination
            page={urlPage}
            pageCount={totalPage}
            onChangePage={onChangePage}
          />

          <ModalRequestAction
            isOpen={!!selectedCustomerIdToBlock}
            icon={images.modalWarning.default}
            textRequest={blockCustomerMessage}
            onCloseModal={onCloseModalBlockUser}
            onConfirm={onConfirmBlockUser}
          />

          <ModalUpgradeCustomerAccount
            isOpen={!!upgradingCustomer}
            onToggleModal={onCloseUpgradeModal}
            user={upgradingCustomer}
            onSubmit={onSubmitUpgradeUser}
          />
        </Container>
      </div>
    </>
  );
};

Customers.propTypes = {
  getCustomers: PropTypes.func.isRequired,
  totalCustomers: PropTypes.number.isRequired,
  customersLoading: PropTypes.bool.isRequired,
  customers: CustomerPropTypes.customers.isRequired,
  provinces: PropTypes.instanceOf(Array).isRequired,
  actionGetProvinceList: PropTypes.func.isRequired,
  exportCustomerFile: PropTypes.func.isRequired,
  exportCustomerLoading: PropTypes.bool.isRequired,
  blockCustomer: PropTypes.func.isRequired,
  unblockCustomer: PropTypes.func.isRequired,
  updateCustomerLevel: PropTypes.func.isRequired,
};

export default Customers;
